import {Component, inject, OnInit} from '@angular/core';
import {ConfigDetailComponent} from "../../../shared/components/config-detail/config-detail.component";
import {
    ConfigDetailSkeletonComponent
} from "../../../shared/components/config-detail-skeleton/config-detail-skeleton.component";
import {DetailBodyComponent} from "../../../shared/components/config-detail/detail-body/detail-body.component";
import {DetailHeaderComponent} from "../../../shared/components/config-detail/detail-header/detail-header.component";
import {EditorComponent} from "ngx-monaco-editor-v2";
import {FormsModule} from "@angular/forms";
import {NgOptimizedImage, UpperCasePipe} from "@angular/common";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {SettingsDataService} from "../../../shared/services/settings-data.service";
import {ProgressService} from "../../../shared/services/progress.service";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ConfigurationService} from "../../../shared/services/configuration.service";
import {ConfigRouteEnum} from "../../../shared/enums/config-route.enum";
import {AppSettingsDetail} from "../models/app-settings.model";
import {AppSettingsDataService} from "../../services/app-settings-data.service";
import {CardModule} from "primeng/card";
import {ButtonModule} from "primeng/button";
import {InputTextModule} from "primeng/inputtext";
import {ToggleSwitch} from "primeng/toggleswitch";

@Component({
    selector: 'app-app-settings-create',
    standalone: true,
    imports: [
        ConfigDetailComponent,
        ConfigDetailSkeletonComponent,
        DetailBodyComponent,
        DetailHeaderComponent,
        EditorComponent,
        FormsModule,
        CardModule,
        ButtonModule,
        InputTextModule,
        ToggleSwitch,
        NgOptimizedImage,
        TranslatePipe,
        UpperCasePipe
    ],
    templateUrl: './app-settings-create.html',
    styleUrl: './app-settings-create.scss',
})
export class AppSettingsCreate implements OnInit {
    protected readonly progressService = inject(ProgressService);
    private readonly settingsDataService = inject(SettingsDataService);
    private readonly snackBar = inject(SnackbarService);
    private readonly translate = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly config = inject(ConfigurationService);
    protected readonly ConfigRouteEnum = ConfigRouteEnum;
    private readonly  appSettingsDataService = inject(AppSettingsDataService);

    protected setting: AppSettingsDetail | undefined = {
        applicationName: '',
        identification: '',
        tenantName: '',
        participantId: '',
        participantPartnerId: '',
        relationType: '',
        applicationType: '',
        applicationSettings: '',
        active: true,
        id: undefined
    };

    protected code: string = '';
    protected originalCode: string = '';
    protected logoUrl: string = this.config.configuration.app.defaultLogo;

    protected editorOptions = {
        language: 'json',
        automaticLayout: true,
        minimap: {enabled: false}
    };

    ngOnInit() {
        const applicationName = this.activatedRoute.snapshot.queryParamMap.get('applicationName');
        const id = this.activatedRoute.snapshot.queryParamMap.get('id');

        if (applicationName) {
            this.setting!.applicationName = applicationName;
            return
        }
        if (!id) {
            return;
        }

        this.appSettingsDataService.getById(Number(id)).subscribe({
            next: (setting) => {
                this.setting = {
                    ...setting,
                    id: undefined
                };
                this.code = setting.applicationSettings
                    ? JSON.stringify(JSON.parse(setting.applicationSettings), null, 2)
                    : '';
                this.originalCode = this.code;
            },
            error: () => {
                this.snackBar.Error(this.translate.instant("snackbar.appSettings.error"));
            }
        });
    }

    protected saveAppSettings() {
        if (!this.setting) {
            return;
        }

        const hasValue = !!(
            this.setting.applicationName ||
            this.setting.identification ||
            this.setting.tenantName ||
            this.setting.participantId ||
            this.setting.participantPartnerId ||
            this.setting.relationType ||
            this.setting.applicationType ||
            this.code
        );

        if (!hasValue) {
            return;
        }

        const newSetting: any = {
            ...this.setting,
            applicationSettings: this.code
        };

        this.settingsDataService.addSetting(newSetting).subscribe({
            next: (response) => {
                this.snackBar.Success(this.translate.instant("snackbar.appSettings.created"));
                this.router.navigate([ConfigRouteEnum.APP_SETTINGS, response.applicationName]);
            },
            error: () => {
                this.snackBar.Error(this.translate.instant("snackbar.appSettings.save_error"));
            }
        });
    }

    protected resetAppSettings() {
        this.code = this.originalCode;
    }
}
